/**
 * Offset pager for the admin lists. The API pages with limit/offset and returns a total, so the
 * console pages without a second call.
 */
import { Button } from './ui/button';
import { PAGE_SIZE } from '@/lib/admin-api';

interface PagerProps {
  offset: number;
  total: number;
  count: number;
  onOffsetChange: (offset: number) => void;
  /** What is being counted, e.g. "reference". Pluralised with a naive trailing "s". */
  noun: string;
}

export function Pager({ offset, total, count, onOffsetChange, noun }: PagerProps) {
  const first = total === 0 ? 0 : offset + 1;
  const last = offset + count;
  const hasPrev = offset > 0;
  const hasNext = last < total;

  if (total <= PAGE_SIZE && !hasPrev) {
    return (
      <p className="mt-3 font-body text-[11.5px] text-ink-quiet" role="status">
        {total} {noun}
        {total === 1 ? '' : 's'}
      </p>
    );
  }

  return (
    <nav aria-label="Pagination" className="mt-4 flex items-center justify-between gap-3">
      <p className="font-body text-[11.5px] text-ink-quiet" role="status">
        {first}&ndash;{last} of {total} {noun}
        {total === 1 ? '' : 's'}
      </p>
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={!hasPrev}
          onClick={() => onOffsetChange(Math.max(0, offset - PAGE_SIZE))}
        >
          Previous
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={!hasNext}
          onClick={() => onOffsetChange(offset + PAGE_SIZE)}
        >
          Next
        </Button>
      </div>
    </nav>
  );
}
